import { useMemo } from 'react';
import { useDonorEta } from '@lp/features/RoutePlanner/hooks/useDonorEta';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { Case, CaseFacility } from '@/api/apis/case';
import { RouteDeviceTracking, RouteTypeEnum } from '@/api/apis/route';
import {
  formatDateTimeString,
  getTxCenterArrivalTime,
  pickLocationDisplayName,
  pickRegionName,
  WithExtendedLocation,
} from '@/utils/helpers';
import { EtaType } from '@/types';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import FacilityCard from './FacilityCard';

type HospitalsBlockProps = {
  donorHospital: CaseFacility;
  txCenter?: CaseFacility;
  deviceTracking?: RouteDeviceTracking;
} & Pick<
  Case,
  | 'donorHospitalDate'
  | 'donorHospitalTime'
  | 'donorHospitalTimeZone'
  | 'recipientHospitalDate'
  | 'recipientHospitalTime'
  | 'recipientHospitalTimeZone'
  | 'txEtaHospitalDate'
  | 'txEtaHospitalTime'
>;

const facilityCardArrivalLabels: Record<string, string> = {
  [EtaType.txCenterSta]: 'STA',
  [EtaType.txCenterEta]: 'ETA',
};

const buildSubLabel = (facility?: WithExtendedLocation<CaseFacility>): string => pickLocationDisplayName(facility);

const buildAvatarName = (facility?: WithExtendedLocation<CaseFacility>): string | undefined => {
  if (!facility) return undefined;

  return facility.code || pickRegionName(facility);
};

export const HospitalsBlock = ({
  donorHospital,
  txCenter,
  deviceTracking,
  donorHospitalTime,
  donorHospitalDate,
  donorHospitalTimeZone,
  recipientHospitalDate,
  recipientHospitalTime,
  recipientHospitalTimeZone,
  txEtaHospitalDate,
  txEtaHospitalTime,
}: HospitalsBlockProps) => {
  const { legs: mainLegs } = useRouteDetails(RouteTypeEnum.Main);
  const { donorEtaString, offsetInMs } = useDonorEta(mainLegs);
  const { extendLocation } = useCountriesData();

  const donorHospitalExtended = useMemo(() => extendLocation(donorHospital), [donorHospital, extendLocation]);

  const txCenterExtended = useMemo(() => (txCenter ? extendLocation(txCenter) : txCenter), [txCenter, extendLocation]);

  const txCenterArrival = useMemo(
    () =>
      getTxCenterArrivalTime({
        txEtaHospitalDate,
        txEtaHospitalTime,
        txCenterTimezone: txCenter?.location.timezone,
        deviceTracking,
        format: 'MM/dd/yyyy HH:mm',
      }),
    [txEtaHospitalDate, txEtaHospitalTime, txCenter, deviceTracking],
  );

  const donorOrTime = useMemo(() => {
    if (!donorHospitalDate) return undefined;
    return formatDateTimeString(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  }, [donorHospitalDate, donorHospitalTime, donorHospitalTimeZone]);

  const txOrTime = useMemo(() => {
    if (!recipientHospitalDate) return undefined;
    return formatDateTimeString(recipientHospitalDate, recipientHospitalTime, recipientHospitalTimeZone);
  }, [recipientHospitalDate, recipientHospitalTime, recipientHospitalTimeZone]);

  return (
    <div className="relative flex flex-col items-center gap-2 w-full">
      {donorHospital && (
        <FacilityCard
          isHospital
          image={donorHospital.logoPath}
          label={donorHospital.name}
          subLabel={buildSubLabel(donorHospitalExtended)}
          orTime={donorOrTime}
          etaTime={offsetInMs ? donorEtaString : undefined}
          etaOffsetTimeMs={offsetInMs}
          avatarProps={{
            fallbackInitials: false,
            name: buildAvatarName(donorHospitalExtended),
          }}
        />
      )}
      <div className="absolute left-4 top-5 bottom-4 flex flex-col items-center">
        <div className="w-4 h-4 border-2 border-black rounded-full flex items-center">
          <p className="text-s-r ml-6">DH</p>
        </div>
        {txCenter && (
          <>
            <div className="h-14 border-r-2 border-dotted border-black" />
            <div className="w-4 h-4 bg-black rounded-full flex items-center">
              <p className="text-s-r ml-6">TXC</p>
            </div>
          </>
        )}
      </div>
      {txCenter && (
        <FacilityCard
          image={txCenter.logoPath}
          label={txCenter.name}
          subLabel={buildSubLabel(txCenterExtended)}
          orTime={txOrTime}
          etaTime={txCenterArrival?.value || undefined}
          etaLabel={txCenterArrival ? facilityCardArrivalLabels[txCenterArrival.type] : 'STA'}
          avatarProps={{
            fallbackInitials: false,
            name: buildAvatarName(txCenterExtended),
          }}
        />
      )}
    </div>
  );
};
